import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { HeartPulse } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import HospitalCombobox from "@/components/HospitalCombobox";
import SpecialtyCombobox from "@/components/SpecialtyCombobox";
import PasswordInput from "@/components/PasswordInput";
import DocumentFields, { validateDocument } from "@/components/DocumentFields";

function routeFor(role) {
  if (role === "doctor" || role === "assistant") return "/panel";
  if (role === "admin") return "/admin";
  return "/buscar";
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextRaw = params.get("next") || "";
  const next = nextRaw.startsWith("/agendar/") ? nextRaw : "";
  const [doc, setDoc] = useState({ type: "", number: "" });
  const [role, setRole] = useState("patient");
  const [form, setForm] = useState({
    name: "", email: "", password: "", password2: "", phone: "",
    specialty: "", center_name: "", exequatur: "",
    birthdate: "", gender: "", address: "", emergency_contact_name: "", emergency_contact_phone: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin) {
      if (form.password !== form.password2) { toast.error("Las contraseñas no coinciden"); return; }
      if (role === "patient" && !form.birthdate) { toast.error("Ingresa tu fecha de nacimiento"); return; }
      if (role === "patient") {
        const docErr = validateDocument(doc.type, doc.number);
        if (docErr) { toast.error(docErr); return; }
      }
    }
    if (!isLogin && role === "doctor") {
      if (!form.specialty || !form.center_name) { toast.error("Selecciona especialidad y hospital"); return; }
      if (!form.exequatur.trim()) { toast.error("Ingresa tu número de exequátur"); return; }
      if (!acceptTerms) { toast.error("Debes aceptar los Términos y la Política de Privacidad"); return; }
    }
    setBusy(true);
    try {
      let user;
      if (isLogin) {
        user = await login(form.email, form.password);
      } else {
        const payload = { name: form.name, email: form.email, password: form.password, phone: form.phone, role };
        if (role === "doctor") {
          payload.specialty = form.specialty;
          payload.center_name = form.center_name;
          payload.exequatur = form.exequatur;
          payload.accept_terms = acceptTerms;
        }
        if (role === "patient") {
          payload.birthdate = form.birthdate;
          payload.gender = form.gender;
          payload.address = form.address;
          payload.emergency_contact_name = form.emergency_contact_name;
          payload.emergency_contact_phone = form.emergency_contact_phone;
          payload.document_type = doc.type;
          payload.document_number = doc.number;
        }
        user = await register(payload);
      }
      toast.success(isLogin ? "Bienvenido de nuevo" : "Cuenta creada");
      if (user.must_change_password) navigate("/cambiar-clave");
      else if (next && user.role === "patient") navigate(next);
      else navigate(routeFor(user.role));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Error");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/buscar";
    if (next) sessionStorage.setItem("auth_next", next);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const commonFields = (
    <>
      <Field label="Nombre completo"><Input required value={form.name} onChange={set("name")} className="rounded-xl border-[#E2E8F0]" data-testid="register-name-input" /></Field>
      <Field label="Correo"><Input required type="email" value={form.email} onChange={set("email")} className="rounded-xl border-[#E2E8F0]" data-testid="auth-email-input" /></Field>
      <Field label="Contraseña"><PasswordInput required minLength={6} value={form.password} onChange={set("password")} className="rounded-xl border-[#E2E8F0]" data-testid="auth-password-input" /></Field>
      <Field label="Confirmar contraseña"><PasswordInput required minLength={6} value={form.password2} onChange={set("password2")} className="rounded-xl border-[#E2E8F0]" data-testid="auth-password2-input" /></Field>
      <Field label="Teléfono (opcional)"><Input value={form.phone} onChange={set("phone")} className="rounded-xl border-[#E2E8F0]" data-testid="register-phone-input" /></Field>
    </>
  );

  const patientFields = (
    <>
      <DocumentFields type={doc.type} number={doc.number} onChange={setDoc} prefix="register-doc" />
      <Field label="Fecha de nacimiento *"><Input required type="date" value={form.birthdate} onChange={set("birthdate")} className="rounded-xl border-[#E2E8F0]" data-testid="register-birthdate-input" /></Field>
      <Field label="Género (opcional)">
        <Select value={form.gender} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
          <SelectTrigger className="rounded-xl border-[#E2E8F0] font-bold" data-testid="register-gender-select"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Femenino">Femenino</SelectItem>
            <SelectItem value="Masculino">Masculino</SelectItem>
            <SelectItem value="Otro">Otro</SelectItem>
            <SelectItem value="Prefiero no decir">Prefiero no decir</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Dirección (opcional)"><Input value={form.address} onChange={set("address")} className="rounded-xl border-[#E2E8F0]" data-testid="register-address-input" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Contacto de emergencia"><Input value={form.emergency_contact_name} onChange={set("emergency_contact_name")} placeholder="Nombre" className="rounded-xl border-[#E2E8F0]" data-testid="register-ec-name-input" /></Field>
        <Field label="Tel. de emergencia"><Input value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} placeholder="Teléfono" className="rounded-xl border-[#E2E8F0]" data-testid="register-ec-phone-input" /></Field>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
        <Link to="/" className="mb-8 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0284C7] text-white">
            <HeartPulse className="h-5 w-5" strokeWidth={2.6} />
          </div>
          <span className="text-lg font-extrabold tracking-tight text-slate-900">SaludCitaRD</span>
        </Link>

        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-8">
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
            {isLogin ? "Iniciar sesión" : "Crear cuenta"}
          </h1>
          <p className="mt-1 text-sm font-semibold text-slate-500">
            {isLogin ? "Accede a tu cuenta de SaludCitaRD" : "Regístrate como paciente o médico"}
          </p>

          {isLogin ? (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Field label="Correo"><Input required type="email" value={form.email} onChange={set("email")} className="rounded-xl border-[#E2E8F0]" data-testid="auth-email-input" /></Field>
              <Field label="Contraseña"><PasswordInput required value={form.password} onChange={set("password")} className="rounded-xl border-[#E2E8F0]" data-testid="auth-password-input" /></Field>
              <div className="text-right">
                <Link to="/olvide-clave" className="text-xs font-bold text-[#0284C7]" data-testid="forgot-link">¿Olvidaste tu contraseña?</Link>
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="auth-submit-btn">
                {busy ? "Procesando…" : "Entrar"}
              </Button>
            </form>
          ) : (
            <Tabs value={role} onValueChange={setRole} className="mt-6">
              <TabsList className="grid w-full grid-cols-2 rounded-full bg-slate-100 p-1">
                <TabsTrigger value="patient" className="rounded-full font-bold data-[state=active]:bg-[#0284C7] data-[state=active]:text-white" data-testid="tab-patient">Paciente</TabsTrigger>
                <TabsTrigger value="doctor" className="rounded-full font-bold data-[state=active]:bg-[#0284C7] data-[state=active]:text-white" data-testid="tab-doctor">Médico</TabsTrigger>
              </TabsList>

              <TabsContent value="patient">
                <form onSubmit={submit} className="mt-5 space-y-4">
                  {commonFields}
                  {patientFields}
                  <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="auth-submit-btn">
                    {busy ? "Procesando…" : "Crear cuenta de paciente"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="doctor">
                <form onSubmit={submit} className="mt-5 space-y-4">
                  {commonFields}
                  <Field label="Especialidad *">
                    <SpecialtyCombobox value={form.specialty} onSelect={(s) => setForm((f) => ({ ...f, specialty: s }))} />
                  </Field>
                  <Field label="Hospital *">
                    <HospitalCombobox value={form.center_name} onSelect={(h) => setForm((f) => ({ ...f, center_name: h.name }))} />
                  </Field>
                  <Field label="Número de exequátur *">
                    <Input required value={form.exequatur} onChange={set("exequatur")} placeholder="Ej. 12345-ABC" className="rounded-xl border-[#E2E8F0]" data-testid="register-exequatur-input" />
                  </Field>
                  <label className="flex items-start gap-2.5 rounded-xl border border-[#E2E8F0] bg-slate-50 p-3">
                    <Checkbox checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} className="mt-0.5" data-testid="accept-terms-checkbox" />
                    <span className="text-xs font-semibold leading-relaxed text-slate-600">
                      Acepto el tratamiento de mis datos según los{" "}
                      <Link to="/terminos" target="_blank" className="font-bold text-[#0284C7] underline" data-testid="terms-link">Términos, Condiciones y la Política de Privacidad</Link>{" "}de SaludCitaRD.
                    </span>
                  </label>
                  <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="auth-submit-btn">
                    {busy ? "Procesando…" : "Crear cuenta de médico"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          )}

          <div className="my-5 flex items-center gap-3 text-xs font-bold uppercase text-slate-400">
            <div className="h-px flex-1 bg-[#E2E8F0]" /> o <div className="h-px flex-1 bg-[#E2E8F0]" />
          </div>
          <Button variant="outline" onClick={googleLogin} className="w-full rounded-full border-[#E2E8F0] font-bold" data-testid="google-login-btn">
            Continuar con Google
          </Button>

          <p className="mt-6 text-center text-sm font-semibold text-slate-500">
            {isLogin ? "¿No tienes cuenta? " : "¿Ya tienes cuenta? "}
            <Link to={`${isLogin ? "/registro" : "/login"}${next ? `?next=${encodeURIComponent(next)}` : ""}`} className="font-bold text-[#0284C7]" data-testid="auth-switch-link">
              {isLogin ? "Regístrate" : "Inicia sesión"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
